import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { Card, PageHeader, Select, Label, Btn } from '../components/Ui';
import { AlertTriangle, FileText, FileX, Inbox, Send, CheckCheck, Copy } from 'lucide-react';

function ymToStr(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

const TILES = [
  { key: 'total_documents', label: 'Total documentos', icon: FileText, color: 'text-ink', accent: 'border-ink' },
  { key: 'needs_review', label: 'Por revisar', icon: AlertTriangle, color: 'text-warn', accent: 'border-warn' },
  { key: 'no_pdf', label: 'Sin PDF', icon: FileX, color: 'text-danger', accent: 'border-danger' },
  { key: 'failed', label: 'Fallidos', icon: Inbox, color: 'text-danger', accent: 'border-danger' },
  { key: 'duplicates', label: 'Duplicados', icon: Copy, color: 'text-warn', accent: 'border-warn' },
  { key: 'ready_to_send', label: 'Por enviar', icon: Send, color: 'text-accent', accent: 'border-accent' },
  { key: 'sent_this_month', label: 'Enviados (mes)', icon: CheckCheck, color: 'text-ok', accent: 'border-ok' },
];

export default function Dashboard() {
  const [period, setPeriod] = useState(ymToStr(new Date()));
  const [companies, setCompanies] = useState([]);
  const [companyCode, setCompanyCode] = useState('');
  const [kpi, setKpi] = useState(null);
  const [recent, setRecent] = useState([]);
  const nav = useNavigate();

  const load = useCallback(async () => {
    const params = { month: period };
    if (companyCode) params.company_code = companyCode;
    const [k, r] = await Promise.all([
      api.get('/api/dashboard/kpi', { params }),
      api.get('/api/documents', { params: { ...params, needs_review: true, limit: 5 } }),
    ]);
    setKpi(k.data);
    setRecent(r.data.items);
  }, [period, companyCode]);

  useEffect(() => {
    api.get('/api/companies').then((r) => setCompanies(r.data));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // build months for select (last 12)
  const months = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    months.push(ymToStr(d));
  }

  return (
    <>
      <PageHeader title="Panel" subtitle={`Periodo ${period}`} testid="dashboard-header">
        <div className="flex items-end gap-3">
          <div>
            <Label>Periodo</Label>
            <Select
              data-testid="kpi-period"
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              className="w-32"
            >
              {months.map((m) => (
                <option key={m} value={m}>{m}</option>
              ))}
            </Select>
          </div>
          <div>
            <Label>Empresa</Label>
            <Select
              data-testid="kpi-company"
              value={companyCode}
              onChange={(e) => setCompanyCode(e.target.value)}
              className="w-44"
            >
              <option value="">Todas</option>
              {companies.map((c) => (
                <option key={c.id} value={c.code}>{c.code} — {c.name}</option>
              ))}
            </Select>
          </div>
        </div>
      </PageHeader>

      <div className="p-8">
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
          {TILES.map(({ key, label, icon: Icon, color, accent }) => (
            <div
              key={key}
              data-testid={`kpi-${key}`}
              className={`border-l-4 ${accent} border-y border-r border-ink/90 p-4 bg-white`}
            >
              <div className="flex items-start justify-between">
                <Icon size={18} className={color} strokeWidth={1.5} />
              </div>
              <div className="num text-4xl font-semibold mt-3 leading-none">
                {kpi ? kpi[key] : '–'}
              </div>
              <div className="text-[10px] font-bold uppercase tracking-wide2 text-muted mt-2">
                {label}
              </div>
            </div>
          ))}
        </div>

        {/* Recent review */}
        <Card className="mt-10" testid="recent-review">
          <div className="border-b border-ink/90 px-4 py-3 flex items-center justify-between">
            <h2 className="text-sm font-bold uppercase tracking-wide2">
              Últimos documentos por revisar
            </h2>
            <Btn variant="secondary" size="sm" onClick={() => nav('/review')} data-testid="goto-review">
              Ver todos
            </Btn>
          </div>
          <table className="w-full text-xs">
            <thead className="bg-surface text-muted uppercase tracking-wide2">
              <tr className="text-[10px]">
                <th className="text-left px-3 py-2 font-bold">Recibido</th>
                <th className="text-left px-3 py-2 font-bold">Fecha factura</th>
                <th className="text-left px-3 py-2 font-bold">Empresa</th>
                <th className="text-left px-3 py-2 font-bold">Proveedor</th>
                <th className="text-left px-3 py-2 font-bold">Asunto</th>
                <th className="text-left px-3 py-2 font-bold">Estado</th>
              </tr>
            </thead>
            <tbody>
              {recent.length === 0 && (
                <tr><td className="px-3 py-6 text-center text-muted" colSpan={6}>Sin documentos por revisar</td></tr>
              )}
              {recent.map((d) => (
                <tr
                  key={d.id}
                  onClick={() => nav(`/documents/${d.id}`)}
                  className="cursor-pointer border-t border-border hover:bg-surface"
                  data-testid={`recent-row-${d.id}`}
                >
                  <td className="px-3 py-2 num whitespace-nowrap">{d.received_at ? new Date(d.received_at).toLocaleDateString() : '—'}</td>
                  <td className="px-3 py-2 num text-muted">{d.document_date || '—'}</td>
                  <td className="px-3 py-2 font-mono">{d.company_code}</td>
                  <td className="px-3 py-2">{d.supplier || '—'}</td>
                  <td className="px-3 py-2 max-w-md truncate">{d.subject || '—'}</td>
                  <td className="px-3 py-2">{d.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      </div>
    </>
  );
}
